import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  InputAdornment,
  Tooltip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Block as BlockIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { useAuth } from '../login/AuthContext';
import { toast } from 'react-toastify';
import axios from 'axios';
import config from '../../config/config';

const UserManagement = () => {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [bayiler, setBayiler] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    KullaniciAdi: '',
    Adi: '',
    Soyadi: '',
    BayiId: '',
    YetkiKodu: 1,
    Sifre: ''
  });

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${config.baseURL}/kullanici`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.data || []);
    } catch (error) {
      toast.error('Kullanıcılar yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const fetchBayiler = async () => {
    try {
      const response = await axios.get(`${config.baseURL}/bayiler`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBayiler(response.data.data || []);
    } catch (error) {
      toast.error('Bayiler yüklenirken bir hata oluştu');
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchBayiler();
  }, []);

  const handleOpenDialog = (user = null) => {
    if (user) {
      setSelectedUser(user);
      setFormData({
        KullaniciAdi: user.KullaniciAdi || '',
        Adi: user.Adi || '',
        Soyadi: user.Soyadi?.trim() || '',
        BayiId: user.BayiId || '',
        YetkiKodu: user.YetkiKodu || 1,
        Sifre: '' // Güvenlik için şifre alanını boş bırak
      });
    } else {
      setSelectedUser(null);
      setFormData({
        KullaniciAdi: '',
        Adi: '',
        Soyadi: '',
        BayiId: '',
        YetkiKodu: 1,
        Sifre: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
    setShowPassword(false);
    setFormData({
      KullaniciAdi: '',
      Adi: '',
      Soyadi: '',
      BayiId: '',
      YetkiKodu: 1,
      Sifre: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    // Validasyonlar
    if (!formData.KullaniciAdi.trim()) {
      toast.error('Kullanıcı adı zorunludur');
      return;
    }
    if (!formData.Adi.trim() || !formData.Soyadi.trim()) {
      toast.error('Ad ve soyad zorunludur');
      return;
    }
    if (!formData.BayiId) {
      toast.error('Bayi seçimi zorunludur');
      return;
    }
    if (!selectedUser && !formData.Sifre) {
      toast.error('Şifre zorunludur');
      return;
    }

    try {
      if (selectedUser) {
        // Güncelleme işlemi
        await axios.put(
          `${config.baseURL}/kullanici/${selectedUser.Id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Kullanıcı başarıyla güncellendi');
      } else {
        // Yeni kullanıcı ekleme
        await axios.post(
          `${config.baseURL}/kullanici`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Kullanıcı başarıyla eklendi');
      }
      handleCloseDialog();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Bir hata oluştu');
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      await axios.patch(
        `${config.baseURL}/kullanici/${userId}/durum`,
        { Aktif: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Kullanıcı durumu güncellendi');
      fetchUsers();
    } catch (error) {
      toast.error('İşlem sırasında bir hata oluştu');
    }
  };

  const filteredUsers = users.filter(user =>
    user.Adi?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.Soyadi?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.KullaniciAdi?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getBayiAdi = (bayiId) => {
    const bayi = bayiler.find(b => b.Id === bayiId);
    return bayi ? bayi.BayiAdi : 'Bilinmeyen Bayi';
  };

  return (
    <Box p={3}>
      <Paper elevation={3} sx={{ p: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5">Kullanıcı Yönetimi</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Yeni Kullanıcı
          </Button>
        </Box>

        <Box display="flex" gap={2} mb={3}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Ara..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchUsers}
          >
            Yenile
          </Button>
        </Box>

        <TableContainer sx={{maxHeight:'400px', overflow:'auto'}} >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Kullanıcı Adı</TableCell>
                <TableCell>Ad Soyad</TableCell>
                <TableCell>Bayi</TableCell>
                <TableCell>İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredUsers.length > 0 ? (
                filteredUsers.map((user) => (
                  <TableRow key={user.Id}>
                    <TableCell>{user.KullaniciAdi}</TableCell>
                    <TableCell>{`${user.Adi} ${user.Soyadi?.trim()}`}</TableCell>
                    <TableCell>{getBayiAdi(user.BayiId)}</TableCell>
                    <TableCell>
                      <Tooltip title="Düzenle">
                        <IconButton
                          color="primary"
                          onClick={() => handleOpenDialog(user)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    Kullanıcı bulunamadı
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedUser ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı Ekle'}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={2}>
            <TextField
              fullWidth
              label="Kullanıcı Adı"
              name="KullaniciAdi"
              value={formData.KullaniciAdi}
              onChange={handleInputChange}
              required
            />
            <TextField
              fullWidth
              label="Ad"
              name="Adi"
              value={formData.Adi}
              onChange={handleInputChange}
              required
            />
            <TextField
              fullWidth
              label="Soyad"
              name="Soyadi"
              value={formData.Soyadi}
              onChange={handleInputChange}
              required
            />
            <TextField
              fullWidth
              label="Şifre"
              name="Sifre"
              type={showPassword ? "text" : "password"}
              value={formData.Sifre}
              onChange={handleInputChange}
              required={!selectedUser}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              helperText={selectedUser ? "Şifre alanını boş bırakırsanız şifre değişmeyecektir" : ""}
            />
            <FormControl fullWidth required>
              <InputLabel>Bayi</InputLabel>
              <Select
                name="BayiId"
                value={formData.BayiId}
                onChange={handleInputChange}
                label="Bayi"
              >
                {bayiler.map((bayi) => (
                  <MenuItem key={bayi.Id} value={bayi.Id}>
                    {bayi.BayiAdi}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>İptal</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {selectedUser ? 'Güncelle' : 'Ekle'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;